import { tokens } from "lib/colors"
import type { CSSProperties } from "react"
import type { CSSObjectWithLabel, GroupBase, StylesConfig } from "react-select"
import { tv } from "tailwind-variants"
import { FIELD_TOKENS, FieldStatus } from "lib/components/Form/fieldStyles"

/** Extends `FIELD_TOKENS` with select-only values; the menu states have no mockup and are the closest existing tokens. */
export const SELECT_TOKENS = {
  ...FIELD_TOKENS,
  white: tokens.neutral[10],
  chipBg: "#E9F0F9",
  optionHover: tokens.neutral[20],
  optionSelected: "#EAF1FC",
  menuShadow: "0 4px 11px rgba(0, 0, 0, 0.1)",
} as const

/** `full` is the default box; `bottom` the underline-only filter-bar look; `none` borderless. */
export type SelectBorder = "full" | "bottom" | "none"

const STATUS_BORDER: Record<FieldStatus, string> = {
  default: SELECT_TOKENS.borderDefault,
  danger: SELECT_TOKENS.borderDanger,
  success: SELECT_TOKENS.borderSuccess,
}

/** Draws the 2px focus ring at 20% of the status colour, the same contract as `fieldBox`'s `ring-2`. */
const STATUS_RING: Record<FieldStatus, string> = {
  default: "0 0 0 2px rgba(47, 121, 222, 0.2)",
  danger: "0 0 0 2px rgba(242, 84, 91, 0.2)",
  success: "0 0 0 2px rgba(0, 180, 155, 0.2)",
}

/**
 * Maps the legacy `noBorder` / `onlyBorderBottom` pair onto a `SelectBorder`. Reproduces the old truth
 * table: `onlyBorderBottom` only takes effect together with `noBorder`, on its own it is still a full box.
 */
export function resolveSelectBorder(noBorder = false, onlyBorderBottom = false): SelectBorder {
  if (!noBorder) return "full"
  return onlyBorderBottom ? "bottom" : "none"
}

export interface SelectControlColors {
  borderColor: string
  hoverBorderColor: string
  boxShadow: string
  backgroundColor: string
}

/**
 * Resolves the control colours with the `fieldBox` truth table: hover goes primary only on the neutral
 * status, focus adds a status-tinted ring, disabled swaps the fill. `bottom` recolours without a ring; `none` rings only.
 */
export function resolveSelectControlColors(input: {
  status: FieldStatus
  disabled: boolean
  isFocused: boolean
  border: SelectBorder
}): SelectControlColors {
  const { status, disabled, isFocused, border } = input
  const backgroundColor = disabled ? SELECT_TOKENS.disabledBg : SELECT_TOKENS.white
  const statusBorder = STATUS_BORDER[status]
  const active = isFocused && !disabled

  if (border === "none") {
    return {
      borderColor: "transparent",
      hoverBorderColor: "transparent",
      boxShadow: active ? STATUS_RING[status] : "none",
      backgroundColor,
    }
  }
  if (border === "bottom") {
    const borderColor = active ? SELECT_TOKENS.borderPrimary : statusBorder
    return { borderColor, hoverBorderColor: borderColor, boxShadow: "none", backgroundColor }
  }
  if (disabled) {
    return { borderColor: statusBorder, hoverBorderColor: statusBorder, boxShadow: "none", backgroundColor }
  }
  if (isFocused) {
    return {
      borderColor: SELECT_TOKENS.borderPrimary,
      hoverBorderColor: SELECT_TOKENS.borderPrimary,
      boxShadow: STATUS_RING[status],
      backgroundColor,
    }
  }
  return {
    borderColor: statusBorder,
    hoverBorderColor: status === "default" ? SELECT_TOKENS.borderPrimary : statusBorder,
    boxShadow: "none",
    backgroundColor,
  }
}

export interface BuildSelectStylesInput {
  status: FieldStatus
  border: SelectBorder
  /** Hides the 1px indicator separator when the host draws its own divider next to the control. */
  hideSeparator?: boolean
}

/**
 * Builds the design-system `styles` for a react-select control (36px box, radius 6, 24px chips, token menu).
 * Layer component rules on top with `mergeStyles` and `customCssLayer` last so call-site overrides win.
 */
export function buildSelectStyles<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ status, border, hideSeparator = false }: BuildSelectStylesInput): StylesConfig<Option, IsMulti, Group> {
  return {
    control: (base, state) => {
      const colors = resolveSelectControlColors({
        status,
        disabled: state.isDisabled,
        isFocused: state.isFocused,
        border,
      })
      return {
        ...base,
        minHeight: 36,
        fontSize: 14,
        borderRadius: border === "bottom" ? 0 : 6,
        borderStyle: "solid",
        borderWidth: border === "full" ? 1 : 0,
        borderBottomWidth: border === "none" ? 0 : 1,
        borderColor: colors.borderColor,
        backgroundColor: colors.backgroundColor,
        boxShadow: colors.boxShadow,
        cursor: state.isDisabled ? "not-allowed" : "default",
        transition: "border-color 150ms, box-shadow 150ms",
        "&:hover": { borderColor: colors.hoverBorderColor },
      }
    },
    // Pinned so a taller context line-height (table cells: 24px) cannot grow the box past 36px.
    valueContainer: (base) => ({ ...base, lineHeight: "20px" }),
    placeholder: (base) => ({ ...base, color: SELECT_TOKENS.placeholder }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? SELECT_TOKENS.placeholder : SELECT_TOKENS.value,
    }),
    input: (base) => ({ ...base, color: SELECT_TOKENS.value }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: SELECT_TOKENS.borderDefault,
      marginTop: 9,
      marginBottom: 9,
      display: hideSeparator || border === "bottom" ? "none" : base.display,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: SELECT_TOKENS.placeholder,
      padding: 8,
      ":hover": { color: SELECT_TOKENS.placeholder },
    }),
    // 6px around the 20px cross keeps the indicator at 32px, so a clearable control stays 36px.
    clearIndicator: (base) => ({
      ...base,
      color: SELECT_TOKENS.placeholder,
      padding: 6,
      ":hover": { color: SELECT_TOKENS.value },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: SELECT_TOKENS.chipBg,
      border: `1px solid ${SELECT_TOKENS.borderDefault}`,
      borderRadius: 4,
      margin: "2px 6px 2px 0",
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      color: state.isDisabled ? SELECT_TOKENS.placeholder : SELECT_TOKENS.value,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: "16px",
      // react-select's base sets `paddingLeft` separately, so the shorthand alone would lose to it.
      padding: "3px 4px 3px 8px",
      paddingLeft: 8,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: SELECT_TOKENS.placeholder,
      paddingLeft: 4,
      paddingRight: 6,
      ":hover": { backgroundColor: "transparent", color: SELECT_TOKENS.value },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      color: SELECT_TOKENS.value,
      borderRadius: 6,
      border: `1px solid ${SELECT_TOKENS.borderDefault}`,
      boxShadow: SELECT_TOKENS.menuShadow,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => {
      let backgroundColor = "transparent"
      if (state.isSelected) backgroundColor = SELECT_TOKENS.optionSelected
      else if (state.isFocused) backgroundColor = SELECT_TOKENS.optionHover
      return {
        ...base,
        padding: "8px 12px",
        fontSize: 14,
        color: state.isDisabled ? SELECT_TOKENS.placeholder : SELECT_TOKENS.value,
        backgroundColor,
        cursor: state.isDisabled ? "not-allowed" : "default",
        ":active": { backgroundColor: state.isDisabled ? undefined : SELECT_TOKENS.optionSelected },
      }
    },
    noOptionsMessage: (base) => ({ ...base, fontSize: 14, color: SELECT_TOKENS.placeholder }),
    loadingMessage: (base) => ({ ...base, fontSize: 14, color: SELECT_TOKENS.placeholder }),
  }
}

/** Wraps call-site `customCss` as the outermost `mergeStyles` layer on `control`; empty when there is nothing to apply. */
export function customCssLayer<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(customCss?: CSSProperties): StylesConfig<Option, IsMulti, Group> {
  if (!customCss) return {}
  return { control: (base) => ({ ...base, ...(customCss as CSSObjectWithLabel) }) }
}

/**
 * Styles the 24px chip rendered by hand in the `+N` summary. A block with a fixed line-height so
 * `text-ellipsis` still truncates; layout classes (`max-w-*`, `flex-1`, `min-w-0`) stay at the call-site.
 */
export const selectChip = tv(
  {
    base: "block h-6 overflow-hidden text-ellipsis whitespace-nowrap rounded border border-neutral-40 bg-[#E9F0F9] px-2 text-xs font-medium leading-[22px]",
    variants: {
      disabled: {
        true: "text-neutral-50",
        false: "text-neutral-70",
      },
    },
    defaultVariants: { disabled: false },
  },
  { twMerge: false },
)
